import { type ReactElement, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { useAuth } from '@/shared/auth/AuthContext';

/**
 * Wraps the application shell, inside `AuthGuard` and around nothing else.
 *
 * Signing in and being scoped to a tenant are two separate facts, and between
 * them there is a real state: the callback has landed, the cookie is sealed, the
 * operator is fully authenticated — and every read the cockpit performs answers
 * 403 `tenantNotSelected`, because the service has no tenant to scope a query
 * to. A shell rendered in that state is a sidebar of badges that cannot count
 * and a top bar that cannot name a customer.
 *
 * So the two guards are two components rather than one with a longer condition.
 * `/select-tenant` sits under `AuthGuard` alone: it needs an identity and must
 * not need a tenant, since needing one is what would make choosing impossible.
 * This guard sits between `AuthGuard` and `AppLayout`, which is the only region
 * where a tenant is genuinely a precondition.
 *
 * `pending` is not handled here. `AuthGuard` has already held the render until
 * the probe answered, so by the time this runs `hasTenant` is a settled fact
 * rather than a not-yet.
 */
export function TenantGuard({ children }: { readonly children: ReactNode }): ReactElement {
  const { hasTenant } = useAuth();
  const location = useLocation();

  if (!hasTenant) {
    /* Carried through the picker the same way `AuthGuard` carries it through the
       sign-in, and for the same reason: an operator who followed a link to a
       case should land on that case once they have said which tenant they are
       working in, not on the dashboard. */
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/select-tenant?next=${encodeURIComponent(next)}`} replace />;
  }

  return <>{children}</>;
}
